import { useContext, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useObject, useIEnvision } from '_common/hooks';
import { useGetCurrentUserQuery } from '_common/services/api/authority';
import { SuiteModalProvider } from '_common/suite';
import { navigateToError403, navigateToError404 } from 'router/history';
import { PERMISSIONS, SuiteContext, SuiteProviderProps } from './context';
import { Editor } from '@tiptap/core';

const DEFAULT_PERMISSIONS: { [Property in ApiSchemas['UserPermissionsValues']]: boolean } = {
  admin: false,
  owner: false,
  access: false,
  edit: false,
  delete: false,
  approve: false,
  comment: false,
  add_permission: false,
  remove_permission: false,
  import: false,
  export: false,
};

const SuiteProvider = ({ manager, children, app, hasTasks }: SuiteProviderProps) => {
  const { id } = useParams<{ id: string }>();
  const {
    data: object,
    refetch,
    error,
  } = useObject({ object_id: id, object_type: app }, { skip: !id });
  const { data: user } = useGetCurrentUserQuery();
  const isIEnvision = useIEnvision();

  const [activeRichText, setActiveRichText] = useState<Editor>();

  const isCommentAuthor = (comment: Presentation.Data.Comment) => {
    if (user && comment) {
      return comment.authorId === user.profile.id;
    }
    return false;
  };

  const isCommentReplyAuthor = (reply: Presentation.Model.Comments.Reply) => {
    if (user && reply) {
      return reply.authorId === user.profile.id;
    }
    return false;
  };

  const isTaskAuthor = (task: Presentation.Data.Task) => {
    if (user && task) {
      return task.authorId === user.profile.id;
    }
    return false;
  };

  const isTaskReplyAuthor = (reply: Presentation.Model.Tasks.Reply) => {
    if (user && reply) {
      return reply.authorId === user.profile.id;
    }
    return false;
  };

  const isTaskAssignee = (task: Presentation.Data.Task) => {
    if (user && task) {
      return task.assignee === user.profile.id;
    }
    return false;
  };

  const permissions = useMemo(() => {
    if (object && user) {
      const permissions = { ...PERMISSIONS };
      const userPermissions = object.user_permissions.reduce(
        (permissions, permission) => {
          permissions[permission] = true;
          return permissions;
        },
        { ...DEFAULT_PERMISSIONS },
      );

      userPermissions.admin = user.is_admin || user.is_superuser;
      // There is nothing that an admin can do that an owner cannot
      const isBossMan = userPermissions.admin || userPermissions.owner;

      const isApproved = object.status === 'approved';

      //#region General
      permissions.canAccess = isBossMan || userPermissions.access;
      permissions.canEdit = isBossMan || userPermissions.edit;
      permissions.canShare = isBossMan;
      permissions.canChangeStatus = isBossMan;
      permissions.canAccessAuditLog = isBossMan;
      //#endregion

      //#region Comments
      permissions.canComment = !isApproved && (isBossMan || userPermissions.comment);
      permissions.canDeleteComment = (comment: Presentation.Data.Comment) =>
        !isApproved && (isBossMan || (isCommentAuthor(comment) && userPermissions.comment));
      permissions.canEditComment = (comment: Presentation.Data.Comment) =>
        !isApproved && isCommentAuthor(comment) && (userPermissions.comment || isBossMan);
      permissions.canResolveComment = (comment: Presentation.Data.Comment) =>
        !isIEnvision &&
        !isApproved &&
        (isBossMan || (isCommentAuthor(comment) && userPermissions.comment));
      permissions.canChangeCommentPriority = (comment: Presentation.Data.Comment) =>
        !isApproved && (isBossMan || (isCommentAuthor(comment) && userPermissions.comment));
      permissions.canEditCommentReply = (reply: Presentation.Model.Comments.Reply) =>
        !isApproved && isCommentReplyAuthor(reply) && (userPermissions.comment || isBossMan);
      permissions.canDeleteCommentReply = (reply: Presentation.Model.Comments.Reply) =>
        !isApproved && (isBossMan || (isCommentReplyAuthor(reply) && userPermissions.comment));

      //#endregion

      //#region Tasks
      permissions.canAccessTasks = !isIEnvision || !!hasTasks;
      permissions.canCreateTask =
        !isIEnvision && !isApproved && (isBossMan || permissions.canComment);
      permissions.canDeleteTask = (task: Presentation.Data.Task) =>
        !isApproved && (isBossMan || isTaskAuthor(task));
      permissions.canEditTask = (task: Presentation.Data.Task) =>
        !isApproved && (isBossMan || isTaskAuthor(task));
      permissions.canChangeTaskStatus = (task: Presentation.Data.Task) =>
        !isApproved && (isBossMan || isTaskAuthor(task) || isTaskAssignee(task));
      permissions.canWatchTask = permissions.canAccess;
      permissions.canEditTaskReply = (reply: Presentation.Model.Tasks.Reply) =>
        !isApproved && (isBossMan || (isTaskReplyAuthor(reply) && userPermissions.comment));
      permissions.canDeleteTaskReply = (reply: Presentation.Model.Tasks.Reply) =>
        !isApproved && (isBossMan || (isTaskReplyAuthor(reply) && userPermissions.comment));
      //#endregion

      //#region Version History
      permissions.canAccessVersionHistory = isBossMan;
      permissions.canSaveVersion = !isApproved && isBossMan;
      permissions.canRestoreVersion = !isApproved && isBossMan;
      //#endregion
      return permissions;
    }
  }, [object, user]);

  const context = useMemo(
    () =>
      //Context is only valid if permissions and object are defined
      permissions && object
        ? {
            permissions,
            object,
            app,
            refetchObject: refetch,
            manager,
            activeRichText,
            setActiveRichText,
          }
        : null,
    [permissions, object, user, refetch, activeRichText, setActiveRichText],
  );

  if (error && 'status' in error) {
    switch (error.status) {
      case 404:
        navigateToError404();
        break;
      case 403:
        navigateToError403();
        break;
    }
  }

  if (context) {
    return (
      <SuiteContext.Provider value={context}>
        {children}
        <SuiteModalProvider manager={manager} />
      </SuiteContext.Provider>
    );
  }
  return null;
};

const useSuite = () => {
  const context = useContext(SuiteContext);
  if (context === undefined) {
    throw new Error('useSuiteObject must be used within a SuiteProvider');
  }
  return context;
};

export const useSuiteApp = () => {
  return useSuite().app;
};

export const useSuiteObject = () => {
  return useSuite().object;
};

export const useSuitePermissions = () => {
  return useSuite().permissions;
};

export const useSuiteUtils = () => {
  return useSuite().refetchObject;
};

export const useSuiteEditor = () => {
  return {
    activeRichText: useSuite().activeRichText,
    setActiveRichText: useSuite().setActiveRichText,
  };
};

export default SuiteProvider;
